/**
 * Future Command Center™ — Sprint 1 Phase 3 (canon v1.0.0, locked).
 *
 * The learner's calm daily home after the Living Report™. Answers four
 * questions — Who am I? (Career DNA™) · What should I do next?
 * (One Next Step™) · Where am I going? (Future Career Matches™,
 * Vision Board™) · How am I growing? (Growth Journey™) — plus YoMA™,
 * Your Mentor Assistant.
 *
 * PRESENTATION ONLY, COMPOSITION ONLY:
 *  - Career DNA™ and recommendation cards are the exact Living Report™
 *    components (CareerDnaCards, RecommendationWhyCard) — no duplicate
 *    presentation, no calculations.
 *  - Report data arrives via the same prefetch/fetch used by /report
 *    (sealed payload verbatim).
 *  - Vision Board™ progress reads the existing `vision_boards` row;
 *    Growth Journey™ reuses fetchMyJourney + buildSchedule. Counting
 *    saved rows for display is presentation, not science.
 *  - Exactly ONE primary action on the page (One Next Step™); every
 *    other section link is quiet text.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, MessageCircle, Sparkles } from "lucide-react";
import PageHead from "@/components/PageHead";
import AppShell from "@/components/shell/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { publishDisplayName } from "@/hooks/useDisplayName";
import { prefetchStudentReport } from "@/lib/reportPrefetch";
import { CareerDnaCards, RecommendationWhyCard } from "@/components/report/LivingReport";
import type { StudentPayload } from "@/components/report/reportTypes";
import { fetchMyJourney, buildSchedule, type ScheduledStep } from "@/lib/journey";
import { useCareerDna } from "@/hooks/useCareerDna";

/* ------------------------------------------------------------------ */
/* Data (existing sources only)                                        */
/* ------------------------------------------------------------------ */

type VisionSnapshot = {
  dreams: number;
  actionsDone: number;
  actionsTotal: number;
  streak: number;
} | null;

function greetingForHour(h: number): string {
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

/**
 * `vision_boards.streak` is jsonb and the Vision Board writes it as
 * `{ count, last }` (older rows are a bare number). Always read a number out
 * of it — rendering the object crashed this page with a React child error.
 */
export function streakCount(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value && typeof value === "object" && "count" in value) {
    const n = Number((value as { count?: unknown }).count);
    return Number.isFinite(n) ? n : 0;
  }
  const n = Number(value);
  return Number.isFinite(n) && value !== null && value !== "" ? n : 0;
}


export default function FutureCommandCenter() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState<string | null>(null);
  const [report, setReport] = useState<StudentPayload | null>(null);
  const [vision, setVision] = useState<VisionSnapshot>(null);
  const [steps, setSteps] = useState<ScheduledStep[] | null>(null);
  const [loading, setLoading] = useState(true);
  const { data: dna } = useCareerDna();

  useEffect(() => {
    // Never sit on a spinner forever: once auth has settled without a user,
    // render the page (its empty states) instead of a blank screen.
    if (!user?.id) {
      if (!authLoading) setLoading(false);
      return;
    }
    let active = true;
    (async () => {
      const [profileR, reportR, visionR, journeyR] = await Promise.allSettled([
        supabase.from("profiles").select("full_name, email").eq("id", user.id).maybeSingle(),
        prefetchStudentReport(user.id),
        supabase
          .from("vision_boards")
          .select("dreams, actions, streak")
          .eq("user_id", user.id)
          .maybeSingle(),
        fetchMyJourney(),
      ]);
      if (!active) return;

      if (profileR.status === "fulfilled") {
        const p = profileR.value.data as { full_name?: string | null; email?: string | null } | null;
        const display = p?.full_name || p?.email?.split("@")[0] || user.email?.split("@")[0] || null;
        publishDisplayName(display);
        setName(display ? display.split(/\s+/)[0] : null);
      }
      if (reportR.status === "fulfilled") {
        const value = reportR.value as StudentPayload | null;
        // Only trust a payload that actually carries the sealed shape we render.
        setReport(value && typeof value === "object" ? value : null);
      }
      if (visionR.status === "fulfilled") {
        const row = visionR.value.data as {
          dreams?: unknown; actions?: unknown; streak?: unknown;
        } | null;
        if (row) {
          const dreams = Array.isArray(row.dreams) ? row.dreams.length : 0;
          const actions = Array.isArray(row.actions) ? (row.actions as { done?: boolean }[]) : [];
          setVision({
            dreams,
            actionsDone: actions.filter((a) => a?.done).length,
            actionsTotal: actions.length,
            streak: streakCount(row.streak),
          });
        }
      }

      if (journeyR.status === "fulfilled") {
        setSteps(buildSchedule(journeyR.value.membership, journeyR.value.milestones));
      }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [user?.id, user?.email, authLoading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" aria-label="Loading your Future Command Center" />
      </div>
    );
  }

  const dnaTop = report?.career_dna?.top_intelligences ?? [];
  const recommendations = report?.recommendations ?? [];
  // The sealed report is the authority, but a walk you finished on this device
  // (or on another one) still counts. `useCareerDna` finds it wherever it is.
  const dnaFallback = dna && !dna.isPreview ? dna : null;
  const completed = steps?.filter((s) => s.status === "completed").length ?? 0;
  const total = steps?.length ?? 0;
  const current = steps?.find((s) => s.status === "current") ?? null;

  return (
    <AppShell title="Future Command Center™">
      <PageHead
        title="Future Command Center™ | WeKIT™ Career Clarity 360"
        description="Your calm daily home — who you are, where you're going, and your one next step."
        path="/command-center"
        noIndex
      />
      <div className="mx-auto max-w-2xl space-y-12">
        {/* 1 · Welcome */}
        <section aria-label="Welcome" className="text-center">
          <h1 className="font-display text-3xl sm:text-4xl font-bold">
            {greetingForHour(new Date().getHours())}{name ? `, ${name}` : ""}.
          </h1>
          <p className="mt-3 text-muted-foreground max-w-prose mx-auto">
            This is your Future Command Center™ — who you are, where you're
            going, and the one step that matters today. All in one calm place.
          </p>
        </section>

        {/* 2 · Who am I? — Career DNA™ */}
        <HomeSection question="Who am I?" title="Career DNA™">
          {dnaTop.length > 0 ? (
            <>
              <CareerDnaCards top={dnaTop} />
              <QuietLink to="/report" label="Re-read your story" />
            </>
          ) : dnaFallback?.archetypePrimary ? (
            <>
              <Card className="border-border/50">
                <CardContent className="p-5 space-y-2">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                    Your lead archetype
                  </p>
                  <p className="font-display text-2xl font-bold">{dnaFallback.archetypePrimary}</p>
                  {dnaFallback.archetypeSecondary && (
                    <p className="text-sm text-muted-foreground">
                      With a strong {dnaFallback.archetypeSecondary} streak alongside it.
                    </p>
                  )}
                </CardContent>
              </Card>
              <QuietLink to="/report" label="Re-read your story" />
            </>
          ) : (
            <MissingReport />
          )}
        </HomeSection>

        {/* 3 · What should I do next? — One Next Step™ (THE primary action) */}
        <section
          aria-label="One Next Step"
          className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 via-background to-accent/10 p-8 text-center"
        >
          <p className="text-[11px] uppercase tracking-[0.2em] text-primary/80 mb-2">
            What should I do next?
          </p>
          <h2 className="font-display text-2xl font-bold">One Next Step™</h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-prose mx-auto">
            One meaningful step forward. Not five. One.
          </p>
          <div className="mt-6 flex justify-center">
            <Button size="lg" onClick={() => navigate("/vision-board")} className="gap-2 rounded-full px-8">
              {vision && vision.dreams > 0 ? "Continue your Vision Board™" : "Build your Vision Board™"}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </section>

        {/* 4 · Where am I going? — Future Career Matches™ */}
        <HomeSection question="Where am I going?" title="Future Career Matches™">
          {recommendations.length > 0 ? (
            <>
              <div className="space-y-3">
                {recommendations.slice(0, 3).map((r) => (
                  <RecommendationWhyCard key={r.career_id} rec={r} />
                ))}
              </div>
              <QuietLink to="/report" label="Explore every match, with its why" />
            </>
          ) : dnaFallback && dnaFallback.matches.length > 0 ? (
            <>
              <div className="space-y-3">
                {dnaFallback.matches.slice(0, 3).map((m) => (
                  <Card key={m.id} className="border-border/50">
                    <CardContent className="p-5 space-y-1">
                      <Link
                        to={`/careers/${encodeURIComponent(m.id)}`}
                        className="font-semibold hover:underline focus-visible:underline"
                      >
                        {m.name}
                      </Link>
                      {m.cluster && (
                        <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                          {m.cluster}
                        </p>
                      )}
                      {m.explanation && (
                        <p className="text-sm text-muted-foreground">{m.explanation}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
              <QuietLink to="/report" label="Explore every match, with its why" />
            </>
          ) : (
            <MissingReport />
          )}
        </HomeSection>

        {/* 5 · Vision Board™ progress */}
        <HomeSection question="Where am I going?" title="Vision Board™">
          {vision ? (
            <Card className="border-border/50">
              <CardContent className="p-5 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-center">
                <Stat value={vision.dreams} label={vision.dreams === 1 ? "dream" : "dreams"} />
                <Stat value={`${vision.actionsDone}/${vision.actionsTotal}`} label="actions taken" />
                <Stat value={vision.streak} label="day streak" />
              </CardContent>
            </Card>
          ) : (
            <p className="text-sm text-muted-foreground text-center">
              Your Vision Board™ is waiting for its first dream — your one next
              step above starts it.
            </p>
          )}
        </HomeSection>

        {/* 6 · How am I growing? — Growth Journey™ */}
        <HomeSection question="How am I growing?" title="Growth Journey™">
          {steps && total > 0 ? (
            <Card className="border-border/50">
              <CardContent className="p-5 text-center space-y-2">
                <p className="font-display text-2xl font-bold">
                  {completed} <span className="text-muted-foreground text-base font-normal">of {total} milestones</span>
                </p>
                {current && (
                  <p className="text-sm text-muted-foreground">
                    Ready now: <span className="text-foreground font-medium">{current.title}</span>
                  </p>
                )}
                <QuietLink to="/journey" label="See your whole journey" />
              </CardContent>
            </Card>
          ) : (
            <p className="text-sm text-muted-foreground text-center">
              Your journey timeline appears here as your membership begins.
            </p>
          )}
        </HomeSection>

        {/* 7 · YoMA™ — Your Mentor Assistant */}
        <HomeSection question="Need to think something through?" title="YoMA™ — Your Mentor Assistant">
          <Card className="border-border/50">
            <CardContent className="p-5 text-center space-y-3">
              <MessageCircle className="mx-auto h-6 w-6 text-primary" aria-hidden="true" />
              <p className="text-sm text-muted-foreground max-w-prose mx-auto">
                YoMA™ is an AI companion — not a human mentor. It's here to
                explain what's in your report, help you reflect, encourage your
                next step, and help you prepare for real mentoring conversations.
              </p>
              <QuietLink to="/coach" label="Talk it through with YoMA™" />
            </CardContent>
          </Card>
        </HomeSection>

        <footer className="pt-4 pb-10 text-center">
          <Sparkles className="mx-auto h-4 w-4 text-primary/50" aria-hidden="true" />
          <p className="mt-2 text-xs text-muted-foreground/60">
            Built from your sealed report — nothing here is recalculated.
          </p>
        </footer>
      </div>
    </AppShell>

  );
}

/* ------------------------------------------------------------------ */

function HomeSection({ question, title, children }: {
  question: string; title: string; children: React.ReactNode;
}) {
  return (
    <section aria-label={title} className="space-y-4">
      <header className="text-center">
        <p className="text-[11px] uppercase tracking-[0.2em] text-primary/80 mb-1">{question}</p>
        <h2 className="font-display text-xl font-bold">{title}</h2>
      </header>
      {children}
    </section>
  );
}

function QuietLink({ to, label }: { to: string; label: string }) {
  return (
    <div className="text-center mt-3">
      <Link to={to} className="text-sm text-primary hover:underline">
        {label} →
      </Link>
    </div>
  );
}

function Stat({ value, label }: { value: number | string; label: string }) {
  return (
    <div>
      <div className="font-display text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function MissingReport() {
  return (
    <p className="text-sm text-muted-foreground text-center">
      Your report isn't ready yet — it appears here the moment it is.{" "}
      <Link to="/start" className="text-primary hover:underline">Take the assessment</Link>
    </p>
  );
}
